// src/modules/tickets/pages/EditTicketPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Avatar,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Select,
  SelectChangeEvent,
  Paper,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Assignment as TicketIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useClients } from "../../../hooks/useClients";

type Client = { _id: string; fullName: string };

export default function EditTicketPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const clients = (useClients() as Client[]) || [];

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${
    theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider
  }`;

  const [jobTypes, setJobTypes] = useState<any[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<Array<{ _id: string; name: string }>>([]);

  const [form, setForm] = useState({
    clientId: "",
    jobType: "",
    deviceType: "",
    brand: "",
    model: "",
    serialOrImei: "",
    notes: "",
    estimatedFinishDate: "",
    status: "pending",
    assignedTo: "",
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ type: "", name: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchAll = async () => {
      try {
        setLoading(true);
        setServerError(null);
        const [ticketRes, jobRes, deviceRes, brandRes, modelRes, techRes] = await Promise.all([
          api.get(`/tickets/${id}`, { headers }),
          api.get("/categories/jobType", { headers }),
          api.get("/categories/deviceType", { headers }),
          api.get("/categories/brand", { headers }),
          api.get("/categories/model", { headers }),
          api.get('/users/technicians', { headers }),
        ]);
        if (!mounted) return;

        const t = ticketRes.data;
        setForm({
          clientId: t.clientId || "",
          jobType: t.jobType || "",
          deviceType: t.deviceType || "",
          brand: t.brand || "",
          model: t.model || "",
          serialOrImei: t.serialOrImei || "",
          notes: t.notes || "",
          estimatedFinishDate: t.estimatedFinishDate ? String(t.estimatedFinishDate).substring(0, 10) : "",
          status: t.status || "pending",
          assignedTo: t.assignedTo?._id || t.assignedTo || "",
        });

        setJobTypes(jobRes.data || []);
        setDeviceTypes(deviceRes.data || []);
        setBrands(brandRes.data || []);
        setModels(modelRes.data || []);
        setTechnicians(techRes.data || []);
      } catch (e) {
        if (!mounted) return;
        setServerError("Failed to load ticket or categories.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name as string]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setServerError(null);
      await api.put(`/tickets/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate(`/tickets/${id}`);
    } catch (err: any) {
      setServerError(err?.response?.data?.message || "Failed to update ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.type || !newCategory.name) return;
    try {
      setSubmitting(true);
      const headers = { Authorization: `Bearer ${token}` };
      await api.post("/categories", newCategory, { headers });
      const res = await api.get(`/categories/${newCategory.type}`, { headers });

      if (newCategory.type === "jobType") setJobTypes(res.data || []);
      if (newCategory.type === "deviceType") setDeviceTypes(res.data || []);
      if (newCategory.type === "brand") setBrands(res.data || []);
      if (newCategory.type === "model") setModels(res.data || []);

      setOpenDialog(false);
      setNewCategory({ type: "", name: "" });
    } catch {
      setServerError("Failed to add category.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
      {/* HERO / HEADER */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <TicketIcon />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Edit Ticket
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Update client/device details and job progress.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/tickets/${id}`)}
                  sx={{ color: "white", fontWeight: 800 }}
                >
                  Back to Ticket
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {serverError}
        </Alert>
      )}

      {/* CONTENT */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: surfaceBorder }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container spacing={2.5}>
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} key={`sk-${i}`}>
                <Skeleton variant="rounded" height={40} />
              </Grid>
            ))
          ) : (
            <>
              {/* Client */}
              <Grid item xs={12}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Client</InputLabel>
                  <Select
                    name="clientId"
                    value={form.clientId}
                    onChange={handleSelectChange}
                    label="Client"
                  >
                    <MenuItem value="">Select Client</MenuItem>
                    {clients.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Category selects */}
              {[
                { label: "Job Type", field: "jobType", data: jobTypes },
                { label: "Device Type", field: "deviceType", data: deviceTypes },
                { label: "Brand", field: "brand", data: brands },
                { label: "Model", field: "model", data: models },
              ].map(({ label, field, data }) => (
                <Grid item xs={12} sm={6} key={field}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>{label}</InputLabel>
                    <Select
                      name={field}
                      value={(form as any)[field]}
                      onChange={handleSelectChange}
                      label={label}
                    >
                      <MenuItem value="">{`Select ${label}`}</MenuItem>
                      {data.map((item: any) => (
                        <MenuItem key={item._id ?? item.name} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
          ))}

          {/* Assign Technician */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Assign Technician (optional)"
              select
              fullWidth
              size="small"
              name="assignedTo"
              value={form.assignedTo || ''}
              onChange={handleSelectChange}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {technicians.map((t) => (
                <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Grid>

              {/* Serial */}
              <Grid item xs={12}>
                <TextField
                  label="Serial / IMEI / Reg #"
                  name="serialOrImei"
                  fullWidth
                  size="small"
                  value={form.serialOrImei}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  label="Diagnostic Notes"
                  name="notes"
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  value={form.notes}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Date + Status */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Finish Date"
                  type="date"
                  name="estimatedFinishDate"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.estimatedFinishDate}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={form.status}
                    onChange={handleSelectChange}
                    label="Status"
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="on-hold">On Hold</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Button
                    onClick={() => setOpenDialog(true)}
                    color="secondary"
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    disabled={submitting}
                  >
                    Add Category
                  </Button>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate(`/tickets/${id}`)} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={submitting}
                      sx={{ fontWeight: 800 }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />

      {/* Add Category Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1.5 }} size="small">
            <InputLabel>Category Type</InputLabel>
            <Select
              value={newCategory.type}
              onChange={(e: SelectChangeEvent) =>
                setNewCategory((prev) => ({ ...prev, type: e.target.value as string }))
              }
              label="Category Type"
            >
              <MenuItem value="jobType">Job Type</MenuItem>
              <MenuItem value="deviceType">Device Type</MenuItem>
              <MenuItem value="brand">Brand</MenuItem>
              <MenuItem value="model">Model</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Category Name"
            fullWidth
            size="small"
            value={newCategory.name}
            onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} variant="contained" color="primary" disabled={submitting}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

